import authService from './authService';

class ScriptPermissionsService {
  async getScripts(scopeType, scopeId, filters = {}) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = '/api/admin/scripts';
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.pageSize) params.append('pageSize', filters.pageSize);
    if (filters.search) params.append('search', filters.search);
    if (filters.ownerId) params.append('ownerId', filters.ownerId);
    if (filters.sharedWith !== undefined) params.append('sharedWith', filters.sharedWith);
    if (filters.isPublic !== undefined) params.append('isPublic', filters.isPublic);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDir) params.append('sortDir', filters.sortDir);

    if (params.toString()) {
      url += (url.includes('?') ? '&' : '?') + params.toString();
    }

    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch scripts: ${response.statusText}`);
    }

    return await response.json();
  }

  async getScriptPermissions(scriptId, scopeType, scopeId) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = `/api/admin/scripts/${scriptId}/permissions`;
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/${scriptId}/permissions?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/${scriptId}/permissions?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch script permissions: ${response.statusText}`);
    }

    return await response.json();
  }

  async getScriptShares(scriptId) {
    const headers = authService.getAuthHeaders();
    const response = await fetch(`/api/baiql/scripts/${scriptId}/shares`, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch script shares: ${response.statusText}`);
    }

    return await response.json();
  }

  async addScriptShare(scriptId, principalType, principalId, role, expiresAt = null) {
    const headers = authService.getAuthHeaders();
    const response = await fetch(`/api/baiql/scripts/${scriptId}/share`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        principalType,
        principalId,
        role,
        expiresAt
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to add script share');
    }

    return await response.json();
  }

  async removeScriptShare(scriptId, shareId, scopeType, scopeId) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = `/api/admin/scripts/${scriptId}/shares/${shareId}`;
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/${scriptId}/shares/${shareId}?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/${scriptId}/shares/${shareId}?teamId=${scopeId}`;
    } else {
      // Fallback to regular endpoint if not admin scope
      const share = await this.getScriptShares(scriptId);
      const targetShare = share.find(s => s.id === shareId);
      if (!targetShare) {
        throw new Error('Share not found');
      }

      return await fetch(`/api/baiql/scripts/${scriptId}/share`, {
        method: 'DELETE',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          principalType: targetShare.principalType,
          principalId: targetShare.principalId
        })
      }).then(res => {
        if (!res.ok) {
          throw new Error('Failed to remove share');
        }
        return res.json();
      });
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to remove script share: ${response.statusText}`);
    }

    return await response.json();
  }

  async bulkShareScripts(scriptIds, principalType, principalId, role, expiresAt, scopeType, scopeId) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = '/api/admin/scripts/bulk-share';
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/bulk-share?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/bulk-share?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        scriptIds,
        principalType,
        principalId,
        role,
        expiresAt
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to bulk share scripts');
    }

    return await response.json();
  }

  async getAuditLog(scopeType, scopeId, filters = {}) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = '/api/admin/scripts/audit-log';
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/audit-log?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/audit-log?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.pageSize) params.append('pageSize', filters.pageSize);
    if (filters.scriptId) params.append('scriptId', filters.scriptId);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.action) params.append('action', filters.action);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    if (params.toString()) {
      url += (url.includes('?') ? '&' : '?') + params.toString();
    }

    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch audit log: ${response.statusText}`);
    }

    return await response.json();
  }

  async getStats(scopeType, scopeId) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = '/api/admin/scripts/stats';
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/stats?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/stats?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch stats: ${response.statusText}`);
    }

    return await response.json();
  }

  async getPermissionsByRole(scopeType, scopeId, role) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = `/api/admin/scripts/permissions-by-role?role=${role}`;
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/permissions-by-role?orgId=${scopeId}&role=${role}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/permissions-by-role?teamId=${scopeId}&role=${role}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch permissions by role: ${response.statusText}`);
    }

    return await response.json();
  }

  async transferOwnership(scriptId, newOwnerId, scopeType, scopeId) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = `/api/admin/scripts/${scriptId}/transfer-ownership`;
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/${scriptId}/transfer-ownership?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/${scriptId}/transfer-ownership?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ newOwnerId })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to transfer ownership');
    }

    return await response.json();
  }

  async bulkRevokePermissions(scriptIds, role, scopeType, scopeId) {
    const headers = authService.getAuthHeaders();
    let url = '';

    if (scopeType === 'Global') {
      url = '/api/admin/scripts/bulk-revoke-permissions';
    } else if (scopeType === 'Organization') {
      url = `/api/enterprise/scripts/bulk-revoke-permissions?orgId=${scopeId}`;
    } else if (scopeType === 'Team') {
      url = `/api/teams/scripts/bulk-revoke-permissions?teamId=${scopeId}`;
    } else {
      throw new Error(`Invalid scopeType: ${scopeType}`);
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ scriptIds, role })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to revoke permissions');
    }

    return await response.json();
  }
}

export default new ScriptPermissionsService();

